// src/hooks/useSEO.js
import { useState, useMemo } from "react";
import { normalizeUrl, looksLikeUrl, copyText } from "../lib/utils";
import { useToast } from "../context/ToastContext";

export function useSEO(seoPackageWebhook, setWebhookLog, LOG_LIMIT) {
  const { setToast } = useToast();
  
  // SEO state
  const [seoUrl, setSeoUrl] = useState("");
  const [seoSubmitting, setSeoSubmitting] = useState(false);
  const [seoResult, setSeoResult] = useState(null);

  // ---------------- SEO ACTIONS ----------------
  const runSeo = async (e) => {
    e?.preventDefault?.();
    if (seoSubmitting) return;

    const raw = (seoUrl || "").trim();
    if (!raw) {
      setToast({ type: "warn", msg: "Paste an article URL first." });
      return;
    }
    const url = normalizeUrl(raw);
    if (!looksLikeUrl(url)) {
      setToast({ type: "warn", msg: "That doesn't look like a valid URL." });
      return;
    }

    setSeoResult(null);
    setSeoSubmitting(true);
    try {
      const resp = await fetch(seoPackageWebhook, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });

      const text = await resp.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
      const item = Array.isArray(data) ? data[0] : data;
      if (!resp.ok || !item?.ok) throw new Error("SEO workflow failed.");

      setSeoResult({
        titles: item.titles || [],
        description: item.description || "",
        tags: item.tags || "",
        titles_count: item.titles_count ?? (item.titles?.length || 0),
        articleUrl: url,
      });

      setWebhookLog((prev) =>
        [{ type: "seo-package", at: Date.now(), data: item }, ...prev].slice(
          0,
          LOG_LIMIT
        )
      );
      setToast({
        type: "ok",
        msg: `SEO package received (${(item.titles || []).length} titles)`,
      });
    } catch (err) {
      setToast({ type: "error", msg: err.message });
    } finally {
      setSeoSubmitting(false);
    }
  };

  const seoAllText = useMemo(() => {
    if (!seoResult) return "";
    const { titles = [], description = "", tags = "", articleUrl = "" } = seoResult;
    const block = [
      articleUrl ? `URL: ${articleUrl}` : "",
      "=== TITLES ===",
      ...titles.map((t, i) => `${i + 1}) ${t}`),
      "",
      "=== DESCRIPTION ===",
      description,
      "",
      `=== TAGS (${(tags || "").length}/500) ===`,
      tags,
    ].join("\n");
    return block.trim();
  }, [seoResult]);

  return {
    seoUrl,
    setSeoUrl,
    seoSubmitting,
    seoResult,
    seoAllText,
    runSeo,
  };
}
